import { ThreadsProfile } from "../../../api/social-account-connect/threads/threads-profile-dto";
import type { BrandKit } from "../brand-kit";

export type AccountPlatformType =
  "IG" | "FB" | "TW" | "PI" | "GMB" | "YT" | "LI" | "SC" | "OT" | "TH" | "TT";

export type PlatformName =
  | "facebook"
  | "googlemybusiness"
  | "instagram"
  | "linkedin"
  | "pinterest"
  | "snapchat"
  | "tiktok"
  | "other"
  | "threads"
  | "twitter"
  | "youtube";

export const platformAccountNameMap: Record<AccountPlatformType, PlatformName> =
  {
    IG: "instagram",
    FB: "facebook",
    TW: "twitter",
    PI: "pinterest",
    TH: "threads",
    GMB: "googlemybusiness",
    YT: "youtube",
    LI: "linkedin",
    SC: "snapchat",
    TT: "tiktok",
    OT: "other",
  };

export enum PolicyEnforcementStatus {
  Ok = "Ok",
  Warning = "Warning",
  Blocked = "Blocked",
}

export type InsightsEmailSettings = {
  enabled?: boolean;
  emailAddresses?: string[];
};

export type MetaAdsCapabilities = {
  reporting: boolean;
  boosting: boolean;
  missingScopes?: string[];
  checkedAt?: string | Date;
};

export type AccountType = {
  _id: string;
  userId?: string;
  displayLogin?: string;
  login: string;
  profilePictureUrl?: string;
  color?: string;
  loginAlias?: string;
  brandName?: string | null;
  changeDefaultThumb?: boolean;
  storyNotificationUsers: string[];
  platformType?: AccountPlatformType | undefined;
  platformId?: string;
  platformName?: string;
  thProfileData?: ThreadsProfile;
  queueSettings?: {
    enabled: boolean;
    schedules: {
      day: number;
      timeSlots: number[];
      _id: string;
    }[];
    notifyEmptyQueue: boolean;
    timezone: string;
  };
  queueEnabled: boolean;
  // FB only
  defaultPage?: {
    id: string;
  };
  // IG with sked link integration
  accountPublicKey?: string | null;

  // NOTE will be true for facebook group accounts
  isGroup?: boolean;
  igTokenAddedAt?: string | boolean;
  insightsOnly?: boolean;
  isMarketingDemo?: boolean;
  promptApiReconnect?: boolean;
  // SKE-4375: ads-only reconnect flag set by Meta ad-account discovery. Kept
  // separate from `promptApiReconnect` so it never affects Insights/Inbox.
  metaAdsReconnectRequired?: boolean;
  metaAdsCapabilities?: MetaAdsCapabilities;
  // SKE-5309: Facebook was connected with only some Pages selected.
  facebookPageLimitedGrant?: boolean;
  // SKE-4527: publishing-only reconnect flag, on the `metaAdsReconnectRequired`
  // precedent. Set when Meta rejects the publish permission for this account.
  // Never conflated with `promptApiReconnect`, which gates Insights and Inbox.
  instagramPublishReconnectRequired?: boolean;
  /** SKE-4807. When a token write last took away a working publishing grant. */
  instagramPublishPermissionLostAt?: string | null;
  /** What the last authorisation, or a first connect without publishing, recorded. */
  instagramReconnectCampaignOutcome?:
    | "updated"
    | "scope_not_granted"
    | "scope_not_offered"
    | "not_covered"
    | "failed";
  // SKE-4527: the Instagram professional account every publish call is
  // addressed to. Present on IG accounts connected through Facebook Login for
  // Business.
  instagram_business_account?: {
    id?: string;
  };
  /** Instagram: the server holds a stored phone login for this account. */
  hasPhoneLogin?: boolean;
  status:
    | "ok"
    | "insightsOnly"
    | "failed"
    | "checking"
    | "reviewing"
    | "waiting"
    | "deactivated"
    | "code"
    | "verifying"
    | "queued"
    | "notApproved"
    | "received";
  url?: {
    enabled: boolean;
  };
  createdAt?: string;
  sessionResynchedAt?: string;
  session?: {
    /** TikTok stores granted OAuth scopes as a comma-separated string. */
    scope?: string;
    scopes?: string[];
    grantedScopes?: string[];
    data_access_expires_at: number;
    expires_at: number;
    issued_at: number;
    refresh_token_expires_at: number;
  };
  /**
   * SKE-4527. Facebook Login for Business writes the granted permissions here
   * rather than onto `session`, depending on which connect path ran, so any
   * scope question has to consult both.
   */
  longSession?: {
    scopes?: string[];
  };

  failReason?: string;
  userDisplayReason?: string;
  legit?: boolean | null;
  failShotURL?: string;
  loginType?: string;
  retrySetting?: string;
  forceComment?: boolean;
  notifyEmptyQueue?: boolean;
  canVerifyEmail?: boolean;
  canVerifySMS?: boolean;
  twoFactorEnabled?: boolean;
  twoFactorKey?: string;

  //TODO need to refactor into notificationSettings
  successNotifications?: string[];
  errorNotifications:
    | boolean
    | {
        enabled: boolean;
        emailAddresses?: string[];
      };
  weeklyInsightsEmail?: InsightsEmailSettings;
  monthlyInsightsEmail?: InsightsEmailSettings;
  utms?: AccountUtms;
  platformPageId?: string;
  linkedinUrn?: string;
  linkedinVanityName?: string;
  piData?: {
    username?: string;
  };
  // X Premium capability metadata (TW accounts). Absent => status unknown.
  twData?: {
    verified?: boolean;
    verified_type?: string;
    subscription_type?: string;
  };
  ttData?: {
    is_business_account?: boolean;
  };
  gmbData?: {
    metadata?: {
      mapsUrl?: string;
      mapsUri?: string;
    };
    title?: string;
    storefrontAddress?: {
      addressLines?: string[];
      locality?: string;
      regionCode?: string;
      postalCode?: string;
    };
  };
  igData?: {
    website?: string;
  };
  notificationSettings?: {
    publish?: {
      enabled: boolean;
      notification: string[];
      email: string[];
    };
    postFailed?: {
      enabled: boolean;
      notification: string[];
      email: string[];
    };
    postSuccess?: {
      enabled: boolean;
      notification: string[];
      email: string[];
    };
  };
  policyEnforcementStatus?: PolicyEnforcementStatus;
  policyEnforcementInfo?: string;
  enableInsights?: boolean;
  userPlan?: string;
  featureToggles?: Record<string, boolean>;
  /**
   * Read-only. The account owner's timezone, decorated by the server.
   * Not used for any client-side scheduling logic — use `user.timezone` instead.
   * Not the same as `queueSettings.timezone` (which controls queue slot times).
   * See docs/timezone-handling.md
   */
  userTimezone?: string;
};

type AccountUtms = {
  enabled: boolean;
  name?: string;
  source?: string;
  medium?: string;
};

export type AccountGroup = {
  title: string;
  groupId?: string;
  accounts: AccountType[];
  brandKit?: BrandKit;
};

export type PostLinkedInAccountType = "personal" | "company" | "";
